export const translations = {
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      why: 'Why w.ai',
      products: 'Products',
      industry: 'Industry',
      contact: 'Contact',
    },
    hero: {
      label: 'PARADIGM ANNOUNCEMENT — 2025',
      line1: 'PHYSICS-DRIVEN',
      line2: 'WHITE BOX AI',
      sub1: 'The First AI That Computes',
      sub2: 'Its Own Architecture',
      emphasis: 'COMPUTES.',
      emphasisSub: 'NOT GUESSES.',
      cta: 'Explore the Paradigm',
    },
    problem: {
      label: '01 — THE PROBLEM',
      title1: 'The Structural Flaw',
      title2: 'of Black-Box AI',
      statements: [
        'Hidden layers are arbitrary.',
        'Neuron counts are heuristic.',
        'Weight initialization is random.',
        'There is no governing equation.',
      ],
      closing: 'That is the crisis.',
    },
    shift: {
      label: '02 — THE SHIFT',
      title: 'A 30-Year Question',
      p1: 'White Box AI began in 1987.',
      p2: 'For more than three decades, one question guided the research:',
      quote:
        'Can artificial intelligence be built on a complete mathematical foundation?',
      answer: 'Today, we answer yes.',
    },
    breakthrough: {
      label: '03 — THE BREAKTHROUGH',
      title: 'Derived, Not Tuned',
      intro: 'White Box AI introduces a physics-closed neural architecture.',
      facts: [
        { prefix: 'Hidden layer depth is ', emphasis: 'computed' },
        { prefix: 'Neuron count is ', emphasis: 'computed' },
        { prefix: 'Initial weights are ', emphasis: 'computed' },
        { prefix: 'Convergence is ', emphasis: 'deterministic' },
        { prefix: 'The system is fully ', emphasis: 'interpretable' },
      ],
      closing1: 'Architecture is no longer guessed.',
      closing2: 'It is derived.',
      watermark: 'DERIVED.',
    },
    declaration: {
      line1: 'AI is no longer a black box.',
      line2: 'It is an engineered physical system.',
      footer: 'WHITE BOX AI — EST. 1987',
    },
    searchOrDerive: {
      title: 'Search or Derive?',
      statLabel: 'Statistical Approximation',
      vs: 'vs',
      mechLabel: 'Mechanistic Representation',
      statSteps: [
        'Observe outcomes.',
        'Adjust parameters.',
        'Reduce error.',
        'Repeat.',
      ],
      statTag: 'Approximate the result.',
      mechSteps: [
        'Identify governing equation.',
        'Differentiate.',
        'Solve directly.',
      ],
      mechTag: 'Derive the structure.',
      transition: 'Two fundamentally different ways\nto approach reality.',
      newtonLabel: 'Newton — 1687',
      fmaLine1:
        'This simple motion can be described\nby many mathematical expressions.',
      fmaLine2: 'Newton derived one governing law.',
      fmaTag: 'Mechanism Replaces Approximation',
      final1: 'Instead of statistically approximating a phenomenon,\u00a0',
      final2: 'White Box AI derives its governing structure.',
    },
    mathClosure: {
      label: '02 — Mathematical Necessity',
      title1: 'Why Industry Needs\u00a0',
      title2: 'Mathematical Closure',
      statements: [
        'Industrial and engineering systems are fundamentally physical processes\nwith a continuous–discrete duality.',
        'Physical systems require differential equations\nto describe constraints and evolution.',
        'Modern sensors provide massive discrete samples.',
        'But discrete samples do not contain the full mechanism.',
        'Without mathematical closure,\nAI remains heuristic.',
        'Industry requires governed models —\nnot empirical approximations.',
      ],
      closing: 'Mathematical closure is industrial necessity.',
    },
    method: {
      label: '03 — The Method',
      title: 'The White Box Method',
      steps: [
        {
          label: 'Industrial Big Data',
          description: 'Discrete, nonlinear, multi-variable observations.',
        },
        {
          label: 'High-Dimensional Empirical Manifold Families',
          description:
            'Empirical curve segments lifted into structured manifolds.',
        },
        {
          label: 'Mechanistic Representation',
          description: 'From statistical samples to governing structure.',
          highlight: true,
        },
        {
          label: '8–12th Order Partial Differential Equations',
          description:
            "Extension of Newton's Second Law\nfrom second-order differential equation\nto higher-order PDE systems,\napproximating coupled manifold dynamics.",
        },
        {
          label: 'Neural Network as Solver',
          description:
            'Structured equations mapped into deep learning architecture,\nsolved via heterogeneous AI computing clusters —\nnot blind curve fitting.',
          terminal: true,
        },
      ],
      closing1: 'Structure first.',
      closing2: 'Computation second.',
    },
    enables: {
      label: '04 — What This Enables',
      title: 'What This Enables',
      cols: [
        {
          heading: 'Interpretability',
          items: [
            'Transparent operational logic',
            'Traceable decision pathways',
            'Engineering knowledge preserved',
            'Regulatory compliance enabled',
          ],
        },
        {
          heading: 'Local Modifiability',
          items: [
            'Parameter-level adjustment without full retraining',
            'Faster industrial iteration',
            'Reduced deployment risk',
            'Long-term maintainability',
          ],
        },
        {
          heading: 'Monotonic Convergence',
          items: [
            'Predictable model behavior',
            'Stable industrial deployment',
            'Reduced stochastic fluctuation',
            'Lower operational uncertainty',
          ],
        },
      ],
      closing1: 'Industrial AI moves',
      closing2: 'from experimental deployment',
      closing3: 'to\u00a0',
      closing4: 'governed infrastructure.',
    },
    about: {
      hero: 'ABOUT',
      missionLabel: 'Our Mission',
      missionTitle1: 'Intelligence that obeys\u00a0',
      missionTitle2: 'physical law.',
      missionP1:
        'White Box AI was founded on a single premise: that artificial intelligence deployed in the physical world must be governed by the same mathematical and physical laws that govern that world.',
      missionP2:
        'We build systems that are not merely accurate — they are structurally correct. Every model we deploy is closed under physics, verifiable by mathematics, and traceable in its reasoning.',
      numbersLabel: 'By the Numbers',
      stat1Label:
        'Years of foundational mathematics, physics, and engineering research',
      stat2Label: 'Industrial-scale successful deployments',
      stat3Label: 'Cumulative measurable economic impact',
      teamLabel: 'Our Team',
      teamSubtitle:
        'A global team of scientists and engineers building the scientific foundation of explainable AI.',
      teamData: {
        founder: {
          name: 'Jiange Wang',
          nameZh: '王坚革',
          title: 'Founder & CEO',
          background:
            'PhD from the University of Tokyo; BS and MS from Tsinghua University. Former Principal Researcher at NTT Group specializing in AI mathematical theory and industrial applications.',
          responsibility:
            'Defines the scientific direction of White Box AI and leads the development and industrial deployment of explainable AI technologies.',
        },
        globalTitle: 'Global Team Structure',
        usTitle: 'US Team — Global Strategy & Commercialization',
        usMembers: [
          {
            name: 'Dengshan Wang',
            nameZh: '王登山',
            title: 'Founder & CEO (US)',
            background:
              'Entrepreneur and strategic leader responsible for America operations and organizational development.',
            responsibility:
              'Leads America market strategy, corporate governance, and international partnerships.',
          },
          {
            name: 'Yueyin Ji',
            nameZh: '季玥音',
            title: 'Co-Founder',
            background:
              "Master's degree from the University of Rochester with a background in business analytics and finance.",
            responsibility:
              'Responsible for overseas commercialization, strategic partnerships, and international market expansion.',
          },
          {
            name: 'Ewa',
            nameZh: 'Ewa',
            title: '',
            background: '',
            responsibility: '',
          },
        ],
        cnTitle: 'China Team — Core Technology & Industrial AI',
        cnMembers: [
          {
            name: 'Linhong Ji',
            nameZh: '季林红',
            title: 'Chief Scientist',
            background:
              'PhD from the University of Tokyo; Professor and doctoral supervisor at Tsinghua University; Chief Scientist of national key engineering projects.',
            responsibility:
              'Provides scientific guidance for long-term research directions and theoretical innovation.',
          },
          {
            name: 'Jianmin Fan',
            nameZh: '樊建民',
            title: 'Head of Operations & Market',
            background:
              'BS and MS from Beijing Information Science & Technology University. Former operations leader at Baidu Autonomous Driving with over 15 years of industry experience.',
            responsibility:
              'Responsible for company operations, market expansion, and industrial AI deployment.',
          },
          {
            name: 'Juguang Wang',
            nameZh: '王炬光',
            title: 'General Manager',
            background:
              "Master's degree in Computer Science from the University of Electro-Communications in Japan with extensive experience in AI platform development and deployment.",
            responsibility:
              'Responsible for company operations, AI platform implementation, and market expansion.',
          },
          {
            name: 'Ruochen Ji',
            nameZh: '季若辰',
            title: 'Product Manager',
            background:
              "PhD and BS from Tsinghua University; Master's from Columbia University GSAPP. Research focuses on spatial systems and interactive technologies.",
            responsibility:
              'Responsible for the full lifecycle management of White Box AI products including roadmap planning and industry applications.',
          },
        ],
        joinTitle: 'Join Our Team',
        joinText:
          'We invite scientists, engineers, and industry experts to collaborate in advancing explainable AI technologies.',
      },
      footerSub: 'Engineering mathematical intelligence for industry.',
    },
    contact: {
      hero: 'CONTACT',
      intro:
        'White Box AI is a physics-closed artificial intelligence architecture developed for all systems. We engage with organizations seeking for real-world deployment.',
      receivedLabel: 'INQUIRY RECEIVED',
      receivedMsg:
        'White Box AI engages with organizations and individuals seeking mathematically & physics-closed AI infrastructure for real-world deployment.',
      directLabel: 'DIRECT INQUIRY',
      directSub:
        'For technical discussion, partnership exploration,\nor implementation inquiries.',
      labelName: 'Name',
      labelOrg: 'Organization',
      labelEmail: 'Email',
      labelMessage: 'Message',
      sending: 'Sending...',
      submit: 'Submit Inquiry',
      error: 'Something went wrong. Please try again or email us directly.',
      footerSub: 'Engineering mathematical intelligence for industry.',
    },
    products: {
      platformLabel: 'White Box AI Platform',
      platformName: 'WB-PLATFORM',
      platformSub: 'White Box AI Development Platform',
      stats: [
        { label: 'Scale', value: '10+ million lines of proprietary code' },
        { label: 'R&D Investment', value: '12M RMB (~1.65M USD)' },
        { label: 'Architecture', value: 'Physics-closed architecture' },
      ],
      descLines: [
        'Core parameters computed via PDE systems.',
        'No hyperparameter tuning.',
      ],
      specs: [
        'DETERMINISTIC CONVERGENCE',
        'FULLY EXPLAINABLE',
        'ZERO RANDOM INITIALIZATION',
      ],
      chipLabel: 'White Box AI Chip',
      chipName: 'WB-CHIP',
      chipSub: 'White Box AI Chip',
      chipLines: [
        'Floating-point precision adaptation architecture.',
        'Embedded White Box AI function core.',
        'Large high-speed on-chip cache.',
        'Reduced dependence on chip data bus bandwidth.',
      ],
      chipQuote:
        'Enables 28nm process nodes\nto achieve high mathematical efficiency\nwithout costly inter-chip interconnect.',
      chipSpecs: [
        'PRECISION-ADAPTIVE FLOATING POINT',
        'EMBEDDED FUNCTION LIBRARY',
        'BUS-BANDWIDTH REDUCTION',
      ],
    },
    industry: {
      sectorLabel: 'SECTORS & APPLICATIONS',
      hero: 'INDUSTRY',
      whereLabel: 'WHERE INTERPRETABILITY IS NOT OPTIONAL',
      sectors: [
        {
          code: 'DEFENSE',
          title: 'Defense & National Security',
          body: 'Mission-critical systems cannot rely on black-box inference. White Box AI provides architectures whose decisions can be traced, verified, and certified — meeting the interpretability standards required for autonomous systems in contested environments.',
          requirement: 'Requirement: Full decision traceability',
        },
        {
          code: 'MEDICAL',
          title: 'Medical & Clinical AI',
          body: 'Regulatory approval demands explainability. White Box AI produces models where every output is derivable from first principles — enabling clinical validation, liability assignment, and FDA/CE pathway compliance.',
          requirement: 'Requirement: Regulatory-grade explainability',
        },
        {
          code: 'ENERGY',
          title: 'Energy & Physical Systems',
          body: 'Physics-governed infrastructure demands physics-governed AI. White Box architectures are derived from the same mathematical language as the systems they model — enabling predictive maintenance, grid optimization, and fault detection with engineering-grade confidence.',
          requirement: 'Requirement: Physics-consistent modeling',
        },
        {
          code: 'FINANCE',
          title: 'Financial Systems',
          body: 'Model risk management requires that every decision be auditable. White Box AI eliminates the opacity that regulators and risk officers cannot accept — providing deterministic, reproducible outputs with complete architectural provenance.',
          requirement: 'Requirement: Deterministic reproducibility',
        },
      ],
    },
  },

  zh: {
    nav: {
      home: '首页',
      about: '关于',
      why: '为何选择 w.ai',
      products: '产品',
      industry: '行业应用',
      contact: '联系我们',
    },
    hero: {
      label: '范式宣言 — 2025',
      line1: '物理驱动',
      line2: 'WHITE BOX AI',
      sub1: '首个自主推导架构的',
      sub2: '人工智能',
      emphasis: '推导。',
      emphasisSub: '而非猜测。',
      cta: '探索这一范式',
    },
    problem: {
      label: '01 — 问题所在',
      title1: '黑盒 AI 的',
      title2: '结构性缺陷',
      statements: [
        '隐藏层数量任意设定。',
        '神经元数量凭经验决定。',
        '权重初始化随机进行。',
        '不存在任何支配方程。',
      ],
      closing: '这就是危机所在。',
    },
    shift: {
      label: '02 — 转变',
      title: '三十年的追问',
      p1: 'White Box AI 始于 1987 年。',
      p2: '三十余年间，一个问题始终引领着这项研究：',
      quote: '人工智能能否建立在完整的数学基础之上？',
      answer: '今天，我们的答案是：是的。',
    },
    breakthrough: {
      label: '03 — 突破',
      title: '推导，而非调参',
      intro: 'White Box AI 引入了一种物理封闭的神经网络架构。',
      facts: [
        { prefix: '隐藏层深度经', emphasis: '推导' },
        { prefix: '神经元数量经', emphasis: '推导' },
        { prefix: '初始权重经', emphasis: '推导' },
        { prefix: '收敛过程', emphasis: '确定可控' },
        { prefix: '系统完全', emphasis: '可解释' },
      ],
      closing1: '架构不再依赖猜测。',
      closing2: '它是推导出来的。',
      watermark: '推导。',
    },
    declaration: {
      line1: 'AI 不再是黑盒。',
      line2: '它是一个经过工程化设计的物理系统。',
      footer: 'WHITE BOX AI — 创立于 1987',
    },
    searchOrDerive: {
      title: '搜索，还是推导？',
      statLabel: '统计近似',
      vs: 'vs',
      mechLabel: '机理表征',
      statSteps: ['观察结果。', '调整参数。', '降低误差。', '反复迭代。'],
      statTag: '近似结果。',
      mechSteps: ['识别支配方程。', '求导。', '直接求解。'],
      mechTag: '推导结构。',
      transition: '两种根本不同的方式，\n以接近现实。',
      newtonLabel: '牛顿 — 1687',
      fmaLine1: '这一简单运动\n可以用多种数学表达式描述。',
      fmaLine2: '牛顿推导出一条支配定律。',
      fmaTag: '机理取代近似',
      final1: '与其对现象进行统计近似，\u00a0',
      final2: 'White Box AI 推导其支配结构。',
    },
    mathClosure: {
      label: '02 — 数学必要性',
      title1: '为何工业需要\u00a0',
      title2: '数学封闭性',
      statements: [
        '工业与工程系统本质上是物理过程，\n具有连续–离散二元性。',
        '物理系统需要微分方程\n来描述约束条件与演化过程。',
        '现代传感器提供海量离散采样数据。',
        '但离散采样并不包含完整的机理信息。',
        '缺乏数学封闭性，\nAI 仍停留于启发式方法。',
        '工业需要受约束的模型——\n而非经验性近似。',
      ],
      closing: '数学封闭性是工业的必然要求。',
    },
    method: {
      label: '03 — 方法论',
      title: 'White Box 方法论',
      steps: [
        {
          label: '工业大数据',
          description: '离散、非线性、多变量观测数据。',
        },
        {
          label: '高维经验流形族',
          description: '经验曲线段提升为结构化流形。',
        },
        {
          label: '机理表征',
          description: '从统计样本到支配结构。',
          highlight: true,
        },
        {
          label: '8–12 阶偏微分方程组',
          description:
            '将牛顿第二定律\n从二阶微分方程\n扩展至高阶偏微分方程组，\n近似描述耦合流形动力学。',
        },
        {
          label: '神经网络作为求解器',
          description:
            '将结构化方程映射至深度学习架构，\n通过异构 AI 计算集群求解——\n而非盲目拟合曲线。',
          terminal: true,
        },
      ],
      closing1: '结构优先。',
      closing2: '计算其次。',
    },
    enables: {
      label: '04 — 由此实现',
      title: '由此实现',
      cols: [
        {
          heading: '可解释性',
          items: [
            '透明的运行逻辑',
            '可追溯的决策路径',
            '工程知识得以保留',
            '满足监管合规要求',
          ],
        },
        {
          heading: '局部可修改性',
          items: [
            '无需完整重训练的参数级调整',
            '更快的工业迭代速度',
            '降低部署风险',
            '长期可维护性',
          ],
        },
        {
          heading: '单调收敛性',
          items: [
            '可预测的模型行为',
            '稳定的工业部署',
            '降低随机波动',
            '降低运营不确定性',
          ],
        },
      ],
      closing1: '工业 AI',
      closing2: '从实验性部署',
      closing3: '迈向\u00a0',
      closing4: '受治理的基础设施。',
    },
    about: {
      hero: '关于',
      missionLabel: '我们的使命',
      missionTitle1: '遵循物理定律的\u00a0',
      missionTitle2: '智能。',
      missionP1:
        'White Box AI 的创立基于一个核心前提：部署于物理世界的人工智能，必须受到支配该世界的数学与物理定律的约束。',
      missionP2:
        '我们构建的系统不仅仅是精确的——它们在结构上是正确的。我们部署的每一个模型都在物理上封闭、在数学上可验证、在推理上可追溯。',
      numbersLabel: '数字说话',
      stat1Label: '年基础数学、物理与工程研究积累',
      stat2Label: '项工业级成功部署案例',
      stat3Label: '累计可量化经济影响',
      teamLabel: '团队介绍',
      teamSubtitle:
        '我们是一支由科学家与工程师组成的全球团队，致力于构建可解释人工智能的科学基础。',
      teamData: {
        founder: {
          name: 'Jiange Wang',
          nameZh: '王坚革',
          title: '创始人兼 CEO',
          background:
            '东京大学博士，清华大学学士与硕士。曾任 NTT 集团人工智能数学理论与场景应用主任研究员。',
          responsibility:
            '负责制定白盒 AI 的技术发展方向，主导可解释人工智能技术体系构建与产业应用落地。',
        },
        globalTitle: '全球团队结构',
        usTitle: '美国团队 — 全球战略与商业化',
        usMembers: [
          {
            name: 'Dengshan Wang',
            nameZh: '王登山',
            title: '美国公司 CEO',
            background: '企业管理与战略负责人，负责美国业务运营与组织发展。',
            responsibility: '负责公司美国战略规划、组织治理以及国际合作拓展。',
          },
          {
            name: 'Yueyin Ji',
            nameZh: '季玥音',
            title: 'Co-Founder',
            background: '罗彻斯特大学硕士，商业分析与金融背景。',
            responsibility:
              '负责白盒 AI 的海外商业化落地、战略合作以及国际市场拓展。',
          },
          {
            name: 'Ewa',
            nameZh: 'Ewa',
            title: '',
            background: '',
            responsibility: '',
          },
        ],
        cnTitle: '中国团队 — 核心技术与产业 AI',
        cnMembers: [
          {
            name: 'Linhong Ji',
            nameZh: '季林红',
            title: '首席科学家',
            background:
              '东京大学博士，清华大学二级教授、博士生导师，国家重点工程项目首席科学家。',
            responsibility: '负责白盒 AI 长期科研方向与理论创新指导。',
          },
          {
            name: 'Jianmin Fan',
            nameZh: '樊建民',
            title: '运营与市场负责人',
            background:
              '北京信息科技大学学士与硕士。曾任百度自动驾驶部门运营负责人，拥有超过15年的行业经验。',
            responsibility:
              '负责公司运营管理、市场拓展以及白盒 AI 产业化应用落地。',
          },
          {
            name: 'Juguang Wang',
            nameZh: '王炬光',
            title: '总经理',
            background:
              '日本电气通信大学计算机专业硕士，在 AI 平台研发与产业化方面拥有丰富经验。',
            responsibility: '负责公司运营管理、AI 平台落地实施以及市场拓展。',
          },
          {
            name: 'Ruochen Ji',
            nameZh: '季若辰',
            title: '产品经理',
            background:
              '清华大学学士与博士，哥伦比亚大学建筑与设计科学硕士，研究方向为空间系统与交互技术。',
            responsibility:
              '负责白盒 AI 产品全生命周期管理，包括产品规划、功能迭代及跨行业应用落地。',
          },
        ],
        joinTitle: '加入我们',
        joinText:
          '我们诚邀科学家、工程师和行业专家加入，共同推动可解释人工智能技术的发展。',
      },
      footerSub: '为工业构建数学智能。',
    },
    contact: {
      hero: '联系我们',
      intro:
        'White Box AI 是一种面向所有系统开发的物理封闭人工智能架构。我们与寻求真实世界部署的组织展开合作。',
      receivedLabel: '已收到询问',
      receivedMsg:
        'White Box AI 与寻求数学及物理封闭 AI 基础设施用于真实世界部署的组织和个人展开合作。',
      directLabel: '直接询问',
      directSub: '欢迎就技术探讨、合作意向\n或实施方案进行咨询。',
      labelName: '姓名',
      labelOrg: '机构',
      labelEmail: '邮箱',
      labelMessage: '留言',
      sending: '发送中...',
      submit: '提交询问',
      error: '出现错误，请重试或直接发送邮件联系我们。',
      footerSub: '为工业构建数学智能。',
    },
    products: {
      platformLabel: 'White Box AI 平台',
      platformName: 'WB-PLATFORM',
      platformSub: 'White Box AI 开发平台',
      stats: [
        { label: '规模', value: '超过 1000 万行专有代码' },
        { label: '研发投入', value: '1200 万人民币（约 165 万美元）' },
        { label: '架构', value: '物理封闭架构' },
      ],
      descLines: ['核心参数通过偏微分方程组推导计算。', '无需超参数调优。'],
      specs: ['确定性收敛', '完全可解释', '零随机初始化'],
      chipLabel: 'White Box AI 芯片',
      chipName: 'WB-CHIP',
      chipSub: 'White Box AI 芯片',
      chipLines: [
        '浮点精度自适应架构。',
        '嵌入式 White Box AI 功能核心。',
        '大容量高速片上缓存。',
        '降低对芯片数据总线带宽的依赖。',
      ],
      chipQuote:
        '使 28nm 制程节点\n无需昂贵的芯片间互连，\n即可实现高数学效率。',
      chipSpecs: ['精度自适应浮点运算', '嵌入式函数库', '总线带宽降低'],
    },
    industry: {
      sectorLabel: '行业与应用',
      hero: '行业',
      whereLabel: '可解释性不可或缺的领域',
      sectors: [
        {
          code: 'DEFENSE',
          title: '国防与国家安全',
          body: '关键任务系统不能依赖黑盒推断。White Box AI 提供决策可追溯、可验证、可认证的架构——满足对抗环境中自主系统所需的可解释性标准。',
          requirement: '要求：完整的决策可追溯性',
        },
        {
          code: 'MEDICAL',
          title: '医疗与临床 AI',
          body: '监管审批要求可解释性。White Box AI 生成的模型中，每个输出均可从第一性原理推导——支持临床验证、责任归属及 FDA/CE 合规路径。',
          requirement: '要求：监管级可解释性',
        },
        {
          code: 'ENERGY',
          title: '能源与物理系统',
          body: '物理支配的基础设施需要物理支配的 AI。White Box 架构与其建模系统采用相同的数学语言推导——以工程级可信度实现预测性维护、电网优化与故障检测。',
          requirement: '要求：物理一致性建模',
        },
        {
          code: 'FINANCE',
          title: '金融系统',
          body: '模型风险管理要求每项决策均可审计。White Box AI 消除了监管机构和风险官员无法接受的不透明性——提供具有完整架构溯源的确定性、可复现输出。',
          requirement: '要求：确定性可复现性',
        },
      ],
    },
  },
} as const

export type Translations = typeof translations
export type Lang = keyof Translations
